// Support Need contracts (NED-*).
//
// A Need owns its FULL help window, independent of its event (NED-002, invariant 2):
// the driver is needed 09:15–13:00 around a 10:15 appointment. The window is required
// even when `eventId` is present — deriving it from the event is the mistake this
// contract exists to make impossible.
//
// The field list is the kernel's `SupportNeed` and the `supportNeeds` table, one for
// one. `meetingDetail` and `practicalNote` are two fields rather than one `notes`,
// because NED-018 asks for practical pickup/meeting instructions specifically while
// discouraging sensitive notes — a single blob cannot be steered that way.

import { z } from "zod";

import type { NeedRejection, Result } from "../errors.js";
import {
  capacitySchema,
  circleIdSchema,
  eventIdSchema,
  helpModeSchema,
  importanceSchema,
  instantSchema,
  needTypeSchema,
  revisionSchema,
  supportNeedIdSchema,
} from "../primitives.js";

const needWindow = {
  /** Required even when eventId is set: a need's window is never inherited. */
  startsAt: instantSchema,
  endsAt: instantSchema,
} as const;

const labelSchema = z.string().min(1, "A need needs a short description.").max(200);
/** Where to meet, how to get in — practical, never clinical (NED-018). */
const meetingDetailSchema = z.string().max(1000);
const practicalNoteSchema = z.string().max(2000);
const helpModesSchema = z.array(helpModeSchema).min(1, "Choose at least one kind of help.").max(3);

/**
 * An optional field on an EDIT: absent leaves it alone, a value sets it, `null`
 * removes it. Without the third case a note that turned out to be wrong could only
 * ever be overwritten with a space.
 */
const clearable = <T extends z.ZodType>(schema: T) => schema.nullable().optional();

export const createNeedInput = z
  .object({
    circleId: circleIdSchema,
    /** Present only when this need hangs off an event (NED-007). */
    eventId: eventIdSchema.optional(),
    label: labelSchema,
    needType: needTypeSchema,
    ...needWindow,
    /** At least one person. `capacity()` in the kernel enforces the floor. */
    requiredCapacity: capacitySchema,
    requiredHelpModes: helpModesSchema,
    importance: importanceSchema,
    meetingDetail: meetingDetailSchema.optional(),
    practicalNote: practicalNoteSchema.optional(),
  })
  .refine((need) => need.endsAt > need.startsAt, {
    message: "A help window must end after it starts.",
    path: ["endsAt"],
  });

export const updateNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  expectedRevision: revisionSchema,
  label: labelSchema.optional(),
  needType: needTypeSchema.optional(),
  startsAt: instantSchema.optional(),
  endsAt: instantSchema.optional(),
  requiredCapacity: capacitySchema.optional(),
  requiredHelpModes: helpModesSchema.optional(),
  importance: importanceSchema.optional(),
  meetingDetail: clearable(meetingDetailSchema),
  practicalNote: clearable(practicalNoteSchema),
});

/** Draft -> open (plan §10.3). Publishing changes no material field. */
export const publishNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  expectedRevision: revisionSchema,
});

/**
 * Mark a need Done (NED-013).
 *
 * `organiserCorrection` is the ONLY way to complete a need before its window has
 * started, and it exists so that the exception leaves a trail rather than being
 * indistinguishable from the ordinary case. Time passing is never itself completion.
 */
export const completeNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
  expectedRevision: revisionSchema,
  organiserCorrection: z.boolean().default(false),
});

/**
 * Reopen a completed need (NED-014, invariant 14).
 *
 * There is no "reopen quietly" option, deliberately: reopening always creates a new
 * revision, so anybody who had agreed is asked again rather than assumed. The kernel
 * can refuse a silent reopen; this contract cannot express one.
 */
export const reopenNeedInput = publishNeedInput;

/**
 * Cancel a need.
 *
 * Cancellation is NOT deletion (invariant 6): the record and its history survive and
 * only the state changes (plan §10.12). Like events, it carries no free-text reason —
 * the `supportNeeds` table has nowhere to store one and the audit row takes codes
 * only.
 */
export const cancelNeedInput = publishNeedInput;

/**
 * Hard-delete a need (plan §10.12).
 *
 * Legal ONLY for a draft nobody has acted on. Everything published is cancelled.
 */
export const deleteDraftNeedInput = publishNeedInput;

export const getNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
});

/**
 * List needs, either across the plan or for one event.
 *
 * The range is over the NEED's window, never its event's — a driver needed 09:15 for
 * an 10:15 appointment belongs in the 09:00 hour, and listing by the appointment
 * would put them in the wrong place (NED-010).
 */
export const listNeedsInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema.optional(),
  fromInclusive: instantSchema.optional(),
  toExclusive: instantSchema.optional(),
});

export const listNeedRevisionsInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
});

export type CreateNeedInput = z.input<typeof createNeedInput>;
export type UpdateNeedInput = z.input<typeof updateNeedInput>;
export type PublishNeedInput = z.input<typeof publishNeedInput>;
export type CompleteNeedInput = z.input<typeof completeNeedInput>;
export type ReopenNeedInput = z.input<typeof reopenNeedInput>;
export type CancelNeedInput = z.input<typeof cancelNeedInput>;
export type DeleteDraftNeedInput = z.input<typeof deleteDraftNeedInput>;
export type GetNeedInput = z.input<typeof getNeedInput>;
export type ListNeedsInput = z.input<typeof listNeedsInput>;
export type ListNeedRevisionsInput = z.input<typeof listNeedRevisionsInput>;

/**
 * Coverage as the UI receives it: a COUNT, never a percentage and never per-hour
 * segment math (NED-012, COV-006, invariant 1). It is derived on read; nothing
 * stores it.
 */
export interface CoverageView {
  readonly requiredCapacity: number;
  readonly confirmedCapacity: number;
  /** The five user-visible states, plus past display handled by the UI. */
  readonly status: "needsSomeone" | "covered" | "pleaseRecheck" | "done" | "cancelled";
  /** Secondary context under "Needs someone" — never itself a coverage state. */
  readonly askedAndWaiting: boolean;
}

/** The four STORED need states (plan §28.3). The coverage states are derived. */
export type NeedStateView = "draft" | "open" | "completed" | "cancelled";

/**
 * A need as the UI receives it.
 *
 * Carries `requiredCapacity` — the number coverage is derived AGAINST — and no
 * `confirmedCapacity`, `covered` or `status` field. Coverage is computed from the
 * need's commitments at read time and arrives separately as a `CoverageView`; a
 * count stored beside the need would be the second, drifting answer COV-002 forbids.
 */
export interface NeedView {
  readonly supportNeedId: string;
  readonly eventId?: string;
  readonly label: string;
  readonly needType: "visit" | "drive" | "accompany" | "call" | "errand" | "other";
  /** The HELP window. Never the event's (NED-002, NED-010). */
  readonly startsAt: number;
  readonly endsAt: number;
  readonly requiredCapacity: number;
  readonly requiredHelpModes: readonly ("in_person" | "driving" | "phone_or_online")[];
  readonly importance: "ordinary" | "important" | "critical";
  readonly meetingDetail?: string;
  readonly practicalNote?: string;
  readonly state: NeedStateView;
  readonly revision: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly cancelledAt?: number;
}

/**
 * One entry of the need's material history (NED-009, plan §28.6).
 *
 * Exactly the four fields whose change invalidates agreement. The label and the
 * practical note are absent because they are cosmetic — and because history is not a
 * second copy of everything a need ever said.
 */
export interface NeedRevisionView {
  readonly revision: number;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly requiredCapacity: number;
  readonly requiredHelpModes: readonly ("in_person" | "driving" | "phone_or_online")[];
  readonly changedByPersonId: string;
  readonly changedAt: number;
}

export interface NeedWritten {
  readonly supportNeedId: string;
  /** Independent of the event's revision (NED-008). */
  readonly revision: number;
  /**
   * Whether this edit disturbed anybody's agreement — the impact summary plan §10.8
   * requires the editor to see BEFORE a material save. Mirrors the kernel's
   * `ChangeClassification["outcome"]` exactly.
   */
  readonly outcome: "no_change" | "cosmetic" | "needs_editor_decision" | "material";
}

/** A deleted draft has no revision to report — the record is gone (plan §10.12). */
export interface NeedDeleted {
  readonly supportNeedId: string;
  readonly deleted: true;
}

export type CreateNeedResult = Result<NeedWritten, NeedRejection>;
export type UpdateNeedResult = Result<NeedWritten, NeedRejection>;
export type PublishNeedResult = Result<NeedWritten, NeedRejection>;
export type CompleteNeedResult = Result<NeedWritten, NeedRejection>;
export type ReopenNeedResult = Result<NeedWritten, NeedRejection>;
export type CancelNeedResult = Result<NeedWritten, NeedRejection>;
export type DeleteDraftNeedResult = Result<NeedDeleted, NeedRejection>;
export type GetNeedResult = Result<NeedView, NeedRejection>;
export type ListNeedRevisionsResult = Result<readonly NeedRevisionView[], NeedRejection>;
